import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Target, 
  TrendingUp, 
  Users, 
  Zap, 
  ArrowRight, 
  CheckCircle, 
  BarChart3, 
  MessageCircle, 
  Phone, 
  Mail, 
  Instagram, 
  MapPin,
  Eye,
  Calendar,
  DollarSign,
  Timer,
  Building2,
  Lightbulb,
  Rocket,
  Award,
  PieChart,
  MousePointer,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Layers,
  Settings,
  UserCheck
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const EnhancedTorqueLandingPage = () => {
  const [formData, setFormData] = useState({
    nome: '',
    empresa: '',
    telefone: '',
    cidade: '',
    interesse: ''
  });

  const [visibleSections, setVisibleSections] = useState(new Set());
  const [currentProject, setCurrentProject] = useState(0);
  const sectionRefs = useRef({});

  // Real Italínea projects with updated metrics
  const projects = [
    {
      id: 1,
      title: "Projeto Residencial 01",
      location: "Belém - PA",
      timeline: "21 dias", 
      description: "Captação Meta Ads + automação WhatsApp + fechamento",
      metrics: { leads: 28, cpl: "R$ 24", conversion: "41%" },
      image: "https://customer-assets.emergentagent.com/job_torque-marketing/artifacts/02dztnxr_image.png",
      type: "Projeto 01"
    },
    {
      id: 2,
      title: "Projeto Residencial 02",
      location: "Belém - PA",
      timeline: "16 dias",
      description: "Metodologia Pipeline Moveleiro™ completa aplicada",
      metrics: { leads: 35, cpl: "R$ 32", conversion: "35%" },
      image: "https://customer-assets.emergentagent.com/job_torque-marketing/artifacts/ivnwz1o8_image.png",
      type: "Projeto 02"
    },
    {
      id: 3,
      title: "Projeto Residencial 03", 
      location: "Belém - PA",
      timeline: "19 dias",
      description: "Lead qualificado até aprovação do projeto via pipeline",
      metrics: { leads: 29, cpl: "R$ 45", conversion: "29%" },
      image: "https://customer-assets.emergentagent.com/job_torque-marketing/artifacts/7mu5nda1_image.png",
      type: "Projeto 03"
    },
    {
      id: 4,
      title: "Projeto Residencial 04",
      location: "Belém - PA", 
      timeline: "20 dias",
      description: "Meta Ads + WhatsApp + CRM integrados para este resultado",
      metrics: { leads: 31, cpl: "R$ 56", conversion: "27%" },
      image: "https://customer-assets.emergentagent.com/job_torque-marketing/artifacts/ta0bjqt8_image.png",
      type: "Projeto 04"
    }
  ];

  // Solutions data - 3 main solutions
  const solutions = [
    {
      name: "Campanhas de Tração",
      headline: "Gere leads qualificados todos os dias",
      description: "Pare de depender do boca a boca. Criamos campanhas no Meta Ads para atrair clientes com perfil de compra — e entregamos tudo no seu CRM, pronto para atendimento.",
      includes: [
        "Estratégia de mídia e segmentação local",
        "Criação de anúncios e copywriting que convertem", 
        "Integração com WhatsApp e CRM avançado",
        "Relatórios semanais de leads e CPL"
      ],
      shortCopy: "Anúncios que geram conversas reais, não só cliques.",
      icon: Target,
      gradient: "from-blue-600 to-blue-500",
      bgColor: "bg-gradient-to-br from-blue-600 to-blue-500"
    },
    {
      name: "Pipeline Moveleiro™ Completo",
      headline: "Implante um processo de vendas no automático",
      description: "O lead entra, o WhatsApp responde, o sistema organiza e sua equipe vende. Conectamos Meta Ads, automação e CRM para não perder oportunidades.",
      includes: [
        "Automação completa de mensagens WhatsApp",
        "Funil de vendas integrado (Notion + RD Station)",
        "Dashboards em tempo real de CPL, conversão e ROI", 
        "Treinamento completo do time comercial"
      ],
      shortCopy: "O funil que trabalha por você, 24 horas por dia.",
      icon: Layers,
      gradient: "from-purple-600 to-purple-500",
      bgColor: "bg-gradient-to-br from-purple-600 to-purple-500"
    },
    {
      name: "Mentoria TORQUE (Sócio Estratégico)",
      headline: "Quer um sócio no crescimento da sua loja?",
      description: "Mentoria prática, feita por quem também é dono de loja. Cresça com método, rotina e indicadores — visão de sócio, não de consultor.",
      includes: [
        "Diagnóstico completo do funil e operação",
        "Metas e KPIs com painel de acompanhamento",
        "Sessões quinzenais por 90 dias com plano de ação",
        "Acesso vitalício a dashboards e templates exclusivos"
      ],
      shortCopy: "Mais que mentoria: um parceiro estratégico que cresce com você.",
      icon: UserCheck,
      gradient: "from-orange-500 to-orange-400",
      bgColor: "bg-gradient-to-br from-orange-500 to-orange-400",
      isHighlight: true
    }
  ];

  // Live dashboard data  
  const [dashboardData, setDashboardData] = useState({
    leadsGerados: 156,
    cplMedio: 20,
    conversao: 38,
    projetosAtivos: 23
  });

  // Video mockup state
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [videoMuted, setVideoMuted] = useState(true);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate projects
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProject(prev => (prev + 1) % projects.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  // Simulate live dashboard updates
  useEffect(() => {
    const interval = setInterval(() => {
      setDashboardData(prev => ({
        ...prev,
        leadsGerados: prev.leadsGerados + Math.floor(Math.random() * 2),
        cplMedio: Math.max(18, Math.min(25, prev.cplMedio + (Math.random() - 0.5) * 1)),
        conversao: Math.max(32, Math.min(45, prev.conversao + (Math.random() - 0.5) * 1.5))
      }));
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Diagnóstico solicitado!",
      description: "Nossa equipe entrará em contato em até 2 horas úteis."
    });
    
    setFormData({
      nome: '',
      empresa: '',
      telefone: '',
      cidade: '',
      interesse: ''
    });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const setSectionRef = (id) => (el) => {
    if (el) sectionRefs.current[id] = el;
  };

  return (
    <div className="min-h-screen">
      {/* Enhanced Header */}
      <header className="bg-white/95 backdrop-blur-lg shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_4c7aa62d-bdd7-432c-87e3-51085f7d8bab/artifacts/kl18ref6_logo-torque-digital.png" 
                alt="TORQUE Digital" 
                className="h-10"
              />
              <Badge variant="secondary" className="bg-slate-100 text-slate-700 hidden md:block font-medium">
                Especialistas em Móveis Planejados
              </Badge>
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('problema')} className="text-slate-600 hover:text-slate-900 font-medium transition-colors relative group">
                O Problema
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 transition-all group-hover:w-full"></span>
              </button>
              <button onClick={() => scrollToSection('metodo')} className="text-slate-600 hover:text-slate-900 font-medium transition-colors relative group">
                Método
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 transition-all group-hover:w-full"></span>
              </button>
              <button onClick={() => scrollToSection('solucoes')} className="text-slate-600 hover:text-slate-900 font-medium transition-colors relative group">
                Soluções
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 transition-all group-hover:w-full"></span>
              </button>
              <button onClick={() => scrollToSection('projetos')} className="text-slate-600 hover:text-slate-900 font-medium transition-colors relative group">
                Projetos Italínea
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-slate-900 transition-all group-hover:w-full"></span>
              </button>
            </nav>
            <Button 
              onClick={() => scrollToSection('contato')}
              className="bg-slate-900 hover:bg-slate-800 text-white px-6 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            >
              <Rocket className="mr-2 h-4 w-4" />
              Diagnóstico Gratuito
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with TORQUE Colors */}
      <section 
        id="hero"
        ref={setSectionRef('hero')}
        className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-24 overflow-hidden"
        style={{background: 'linear-gradient(135deg, #0F2284 0%, #103876 50%, #2E2E2E 100%)'}}
      >
        {/* Dynamic gradient overlay */}
        <div className="absolute inset-0 opacity-20" style={{background: 'linear-gradient(45deg, #406AD9 0%, #298DEC 100%)'}}></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-72 h-72 rounded-full blur-3xl animate-pulse opacity-30" style={{background: 'linear-gradient(135deg, #406AD9, #298DEC)'}}></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse delay-1000 opacity-20" style={{background: 'linear-gradient(135deg, #FF9C73, #FFD0B9)'}}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className={`space-y-8 ${visibleSections.has('hero') ? 'animate-fade-in-up' : 'opacity-0'}`}>
                <div className="space-y-4">
                  <Badge className="bg-slate-800/50 text-slate-300 border-slate-700/50 mb-4 text-sm backdrop-blur-sm">
                    🚀 Somos lojistas antes de sermos consultores
                  </Badge>
                  <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                    <span className="block text-white">Se não vende,</span>
                    <span className="block text-transparent bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 bg-clip-text animate-pulse">
                      não é criativo.
                    </span>
                  </h1>
                </div>
                
                <p className="text-xl lg:text-2xl text-slate-300 leading-relaxed max-w-2xl">
                  Campanhas que conectam <strong className="text-white">Meta Ads</strong>, <strong className="text-white">WhatsApp</strong> e <strong className="text-white">CRM</strong> em um só funil — 
                  transformando leads em contratos com o <strong className="text-slate-200">Pipeline Moveleiro™</strong>
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    onClick={() => scrollToSection('projetos')}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-8 py-4 text-lg font-semibold transform hover:scale-105 transition-all duration-200 shadow-2xl hover:shadow-cyan-500/25"
                  >
                    <Eye className="mr-2 h-5 w-5" />
                    Ver Projetos Italínea
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    onClick={() => scrollToSection('dashboard')}
                    className="bg-transparent border-purple-400/50 text-purple-200 hover:bg-purple-600/20 hover:border-purple-300 px-8 py-4 text-lg backdrop-blur-sm"
                  >
                    <BarChart3 className="mr-2 h-5 w-5" />
                    Dashboard ao Vivo
                  </Button>
                </div>

                {/* Refined Stats */}
                <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-700/50">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-200">R$ 17-24</div>
                    <div className="text-sm text-slate-400">CPL Médio</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-200">38%</div>
                    <div className="text-sm text-slate-400">Conversão</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-200">+4x</div>
                    <div className="text-sm text-slate-400">ROI Médio</div>
                  </div>
                </div>
              </div>

              {/* Refined Video Mockup */}
              <div className={`relative ${visibleSections.has('hero') ? 'animate-fade-in-up delay-300' : 'opacity-0'}`}>
                <div className="relative bg-slate-800/40 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/30 shadow-2xl">
                  <div className="absolute top-4 left-4">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>

                  <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl mt-8 h-80 flex items-center justify-center border border-slate-700/50">
                    <div className="text-center space-y-4">
                      <div className="w-20 h-20 bg-slate-700/50 rounded-full flex items-center justify-center mx-auto border border-slate-600/50">
                        <Play className="h-10 w-10 text-slate-300 ml-1" />
                      </div>
                      <div className="space-y-2">
                        <p className="text-white font-semibold">Pipeline Moveleiro™ em Ação</p>
                        <p className="text-slate-400 text-sm">Veja como transformamos leads em vendas reais</p>
                      </div>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-slate-900/80 backdrop-blur-sm rounded-lg p-3 flex items-center justify-between border border-slate-700/50">
                        <div className="flex items-center space-x-3">
                          <button 
                            onClick={() => setVideoPlaying(!videoPlaying)}
                            className="text-white hover:text-slate-300 transition-colors"
                          >
                            {videoPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                          </button>
                          <div className="w-32 h-1 bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-slate-400 rounded-full w-1/3"></div>
                          </div>
                          <span className="text-white text-xs">0:45 / 2:30</span>
                        </div>
                        <button 
                          onClick={() => setVideoMuted(!videoMuted)}
                          className="text-white hover:text-slate-300 transition-colors"
                        >
                          {videoMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute -bottom-4 -right-4 bg-slate-800 text-white p-4 rounded-lg shadow-xl border border-slate-700/50">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">Ao Vivo: 156 leads</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vibrant Gradient Transition */}
      <div className="h-24 bg-gradient-to-b from-blue-900 via-indigo-100 to-gray-50"></div>

      {/* Problem Section with Subtle Background */}
      <section 
        id="problema"
        ref={setSectionRef('problema')}
        className="py-24 bg-gray-50 relative overflow-hidden"
      >
        <div className="container mx-auto px-6 relative z-10">
          <div className={`max-w-6xl mx-auto ${visibleSections.has('problema') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-blue-50 text-blue-700 border-blue-200 mb-6">
                ⚠️ O maior problema do setor
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">
                Você investe em anúncios, mas não sabe
                <span className="block text-blue-600">se o dinheiro volta?</span>
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-8">
                <div className="bg-white border border-blue-100 p-6 rounded-xl shadow-sm">
                  <p className="text-xl text-slate-700 leading-relaxed mb-4">
                    A maioria das lojas de planejados enfrentam o mesmo desafio: 
                  </p>
                  <p className="text-xl font-semibold text-blue-600">
                    "Campanhas que geram volume, mas não vendas."
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[
                    { icon: Target, text: "Campanhas genéricas", problem: "Atraem curiosos, não compradores" },
                    { icon: DollarSign, text: "Sem funil estruturado", problem: "Leads ficam perdidos no processo" },
                    { icon: Timer, text: "Follow-up manual", problem: "Vendedor sobrecarregado, lead esfria" },
                    { icon: BarChart3, text: "Sem métricas reais", problem: "Impossível saber o que funciona" }
                  ].map((item, index) => (
                    <Card key={index} className="p-4 bg-white border-gray-200 hover:shadow-lg transition-all duration-300 hover:border-slate-300">
                      <CardContent className="p-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center">
                            <item.icon className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="font-semibold text-slate-800">{item.text}</span>
                        </div>
                        <p className="text-sm text-slate-600 ml-11">{item.problem}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="bg-white border border-blue-100 p-6 rounded-xl shadow-sm">
                  <p className="text-lg text-slate-700 font-medium">
                    💡 <strong className="text-slate-900">Aqui também somos lojistas, com 2 operações ativas.</strong> E abriremos a caixa preta do que funciona e não funciona.
                  </p>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/834892/pexels-photo-834892.jpeg"
                  alt="Plantas e rascunhos de projetos"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-sm bg-slate-900/70 px-3 py-1 rounded-full backdrop-blur-sm">
                    Projetos reais desenvolvidos pela TORQUE + Italínea
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Transition */}
      <div className="h-16 bg-gradient-to-b from-gray-50 to-white"></div>

      {/* Pipeline Method Section */}
      <section 
        id="metodo" 
        ref={setSectionRef('metodo')}
        className="py-24 bg-white"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-7xl mx-auto ${visibleSections.has('metodo') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-20">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                🎯 Nossa solução exclusiva
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                O método criado por quem vive
                <span className="block text-slate-700">o varejo de planejados</span>
              </h2>
              <p className="text-xl text-slate-600 mb-4 max-w-3xl mx-auto">
                O <span className="font-bold text-slate-900 bg-slate-100 px-2 py-1 rounded">Pipeline Moveleiro™</span> transforma anúncios em contratos assinados.
              </p>
            </div>

            <div className="grid lg:grid-cols-5 gap-8 mb-16">
              {[
                {
                  number: "1",
                  title: "Campanhas Segmentadas",
                  description: "Meta Ads direcionado para quem realmente compra móveis planejados",
                  icon: Target,
                  color: "bg-gradient-to-br from-blue-600 to-blue-500",
                  shadowColor: "hover:shadow-blue-500/30",
                  delay: "delay-0"
                },
                {
                  number: "2", 
                  title: "Organização Visual",
                  description: "Kanban no Notion: cada lead tem status claro e follow-up automático",
                  icon: BarChart3,
                  color: "bg-gradient-to-br from-purple-600 to-purple-500",
                  shadowColor: "hover:shadow-purple-500/30",
                  delay: "delay-100"
                },
                {
                  number: "3",
                  title: "Qualificação Inteligente",
                  description: "WhatsApp automatizado filtra interesse real e agenda visitas",
                  icon: MessageCircle,
                  color: "bg-gradient-to-br from-green-600 to-green-500",
                  shadowColor: "hover:shadow-green-500/30",
                  delay: "delay-200"
                },
                {
                  number: "4",
                  title: "Lead Aquecido",
                  description: "Cliente chega na loja informado, com interesse real no projeto",
                  icon: Users,
                  color: "bg-gradient-to-br from-orange-500 to-orange-400",
                  shadowColor: "hover:shadow-orange-500/30",
                  delay: "delay-300"
                },
                {
                  number: "5",
                  title: "Métricas Transparentes",
                  description: "Dashboard mostra ROI, conversão e performance em tempo real",
                  icon: TrendingUp,
                  color: "bg-gradient-to-br from-pink-600 to-pink-500",
                  shadowColor: "hover:shadow-pink-500/30",
                  delay: "delay-400"
                }
              ].map((step, index) => {
                const IconComponent = step.icon;
                return (
                  <Card key={index} className={`relative bg-white border hover:shadow-xl ${step.shadowColor} transition-all duration-500 group hover:-translate-y-2 ${step.delay}`}>
                    <CardContent className="p-8 text-center h-full flex flex-col">
                      {/* Colorful Connection Line */}
                      {index < 4 && (
                        <div className="absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 hidden lg:block z-0">
                          <div className="absolute -right-1 -top-1 w-3 h-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                        </div>
                      )}
                      
                      <div className={`w-16 h-16 ${step.color} text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        {step.number}
                      </div>
                      
                      <IconComponent className="h-8 w-8 mx-auto mb-4 text-slate-600 group-hover:text-blue-600 transition-colors" />
                      <h3 className="font-bold text-lg text-slate-800 mb-4 group-hover:text-blue-900 transition-colors">{step.title}</h3>
                      <p className="text-sm text-slate-600 flex-grow">{step.description}</p>
                      
                      <div className="mt-4 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full ${step.color} transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700`}></div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center">
              <Button 
                size="lg"
                onClick={() => scrollToSection('solucoes')}
                className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-4 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <Rocket className="mr-2 h-5 w-5" />
                Ver Nossas Soluções
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Transition */}
      <div className="h-16 bg-gradient-to-b from-white to-gray-50"></div>

      {/* Solutions Section - NEW */}
      <section 
        id="solucoes"
        ref={setSectionRef('solucoes')}
        className="py-24 bg-gray-50"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-7xl mx-auto ${visibleSections.has('solucoes') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                🎯 Nossas Soluções
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Três soluções práticas para donos de loja
                <span className="block text-slate-700">que querem vender mais</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto mb-8">
                Cada produto da TORQUE resolve um ponto crítico do seu negócio: da geração de leads até a gestão comercial completa.
              </p>
              <p className="text-lg text-slate-500 max-w-3xl mx-auto">
                Enquanto outras agências vendem anúncios, nós resolvemos o processo. Do lead à venda — método criado por quem vive o mercado de planejados.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {solutions.map((solution, index) => {
                const IconComponent = solution.icon;
                return (
                  <Card 
                    key={index} 
                    className={`relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                      solution.isHighlight 
                        ? solution.bgColor + ' text-white' 
                        : 'bg-white'
                    }`}
                  >
                    {solution.isHighlight && (
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-white/20 text-white border-white/30">
                          ⭐ Mais Completo
                        </Badge>
                      </div>
                    )}

                    <CardContent className="p-8">
                      <div className={`w-16 h-16 bg-gradient-to-br ${solution.gradient} text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                        <IconComponent className="h-8 w-8" />
                      </div>

                      <h3 className={`text-xl font-bold mb-4 ${solution.isHighlight ? 'text-white' : 'text-slate-900'}`}>
                        {solution.name}
                      </h3>
                      
                      <p className={`text-lg font-semibold mb-4 ${solution.isHighlight ? 'text-slate-200' : 'text-slate-800'}`}>
                        {solution.headline}
                      </p>
                      
                      <p className={`text-sm mb-6 leading-relaxed ${solution.isHighlight ? 'text-slate-300' : 'text-slate-600'}`}>
                        {solution.description}
                      </p>

                      <div className="space-y-3 mb-8">
                        <p className={`text-sm font-medium ${solution.isHighlight ? 'text-slate-200' : 'text-slate-700'}`}>
                          ✅ Inclui:
                        </p>
                        {solution.includes.map((item, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${solution.isHighlight ? 'text-white' : 'text-green-600'}`} />
                            <span className={`text-sm ${solution.isHighlight ? 'text-slate-300' : 'text-slate-600'}`}>
                              {item}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className={`text-xs italic mb-6 ${solution.isHighlight ? 'text-slate-400' : 'text-slate-500'}`}>
                        {solution.shortCopy}
                      </div>

                      <Button 
                        className={`w-full ${
                          solution.isHighlight
                            ? 'bg-white text-slate-900 hover:bg-slate-100 hover:scale-105' 
                            : `bg-gradient-to-r ${solution.gradient} text-white hover:shadow-lg hover:scale-105`
                        } transition-all duration-300 font-semibold`}
                        onClick={() => window.open(`https://wa.me/559184070470?text=Quero%20saber%20mais%20sobre%20${encodeURIComponent(solution.name)}`, '_blank')}
                      >
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Quero esta solução
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gradient Transition */}
      <div className="h-16 bg-gradient-to-b from-gray-50 to-white"></div>

      {/* Projects Section with Enhanced Grid */}
      <section 
        id="projetos"
        ref={setSectionRef('projetos')}
        className="py-24 bg-white relative overflow-hidden"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-7xl mx-auto ${visibleSections.has('projetos') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                🏆 Projetos Reais Italínea
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Projetos que saíram do
                <span className="block text-slate-700">nosso próprio Pipeline Moveleiro™</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Cada projeto aplicou nossa metodologia completa: da campanha Meta Ads ao contrato assinado
              </p>
            </div>

            {/* Featured Project Display */}
            <div className="mb-16">
              <Card className="overflow-hidden bg-white border-0 shadow-xl">
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-2">
                    <div className="relative">
                      <img 
                        src={projects[currentProject].image}
                        alt={projects[currentProject].title}
                        className="w-full h-96 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/60 to-transparent"></div>
                      <div className="absolute top-6 left-6">
                        <Badge className="bg-slate-800 text-white border-0">
                          {projects[currentProject].type}
                        </Badge>
                      </div>
                      <div className="absolute bottom-6 left-6 text-white">
                        <p className="text-2xl font-bold">{projects[currentProject].title}</p>
                        <p className="text-slate-200">{projects[currentProject].location}</p>
                      </div>
                      <div className="absolute top-6 right-6">
                        <div className="bg-slate-800 text-white px-3 py-1 rounded-full text-xs font-medium">
                          Italínea
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-12 flex flex-col justify-center">
                      <div className="mb-8">
                        <h3 className="text-3xl font-bold text-slate-900 mb-4">{projects[currentProject].title}</h3>
                        <p className="text-lg text-slate-600 mb-4">{projects[currentProject].description}</p>
                        <p className="text-slate-600 italic mb-6">{projects[currentProject].details}</p>
                        
                        <div className="flex items-center space-x-2 mb-6">
                          <Calendar className="h-5 w-5 text-slate-600" />
                          <span className="font-semibold">Timeline: {projects[currentProject].timeline}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-6 mb-8">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-700">{projects[currentProject].metrics.leads}</div>
                          <div className="text-sm text-slate-600">Leads Gerados</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-700">{projects[currentProject].metrics.cpl}</div>
                          <div className="text-sm text-slate-600">CPL</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-slate-700">{projects[currentProject].metrics.conversion}</div>
                          <div className="text-sm text-slate-600">Conversão</div>
                        </div>
                      </div>

                      <Button 
                        onClick={() => scrollToSection('contato')}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        <Award className="mr-2 h-4 w-4" />
                        Quero Resultados Assim
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Project Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer bg-white border" onClick={() => setCurrentProject(index)}>
                  <div className="relative">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 text-white">
                      <p className="font-bold">{project.title}</p>
                      <p className="text-xs text-slate-300">{project.location}</p>
                    </div>
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-slate-800 text-white text-xs">Italínea</Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-slate-600 mb-4 text-sm">{project.details}</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-medium">{project.metrics.cpl} CPL</span>
                      <span className="text-slate-700 font-medium">{project.metrics.conversion} conversão</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vibrant Gradient Transition */}
      <div className="h-24 bg-gradient-to-b from-white via-purple-100 to-indigo-900"></div>

      {/* Dashboard Section with Vibrant Dark Theme */}
      <section 
        id="dashboard"
        ref={setSectionRef('dashboard')}
        className="py-24 bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-10 right-10 w-64 h-64 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-80 h-80 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className={`max-w-7xl mx-auto ${visibleSections.has('dashboard') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-slate-800 text-slate-300 border-slate-700 mb-6">
                📊 Pipeline Moveleiro™ em tempo real
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Dashboard Ao Vivo
                <span className="block text-slate-300">Transparência Total</span>
              </h2>
              <p className="text-xl text-slate-400 max-w-3xl mx-auto">
                Veja como o Pipeline Moveleiro™ está performando neste exato momento
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 mb-16">
              {[
                {
                  title: "Leads Gerados",
                  value: dashboardData.leadsGerados,
                  change: "+8",
                  icon: Target,
                  color: "text-slate-300",
                  bg: "bg-slate-800/50",
                  border: "border-slate-700/50"
                },
                {
                  title: "CPL Médio",
                  value: `R$ ${Math.round(dashboardData.cplMedio)}`,
                  change: "↓ R$ 2",
                  icon: DollarSign,
                  color: "text-slate-300", 
                  bg: "bg-slate-800/50",
                  border: "border-slate-700/50"
                },
                {
                  title: "Taxa de Conversão",
                  value: `${Math.round(dashboardData.conversao)}%`,
                  change: "+3.2%",
                  icon: TrendingUp,
                  color: "text-slate-300",
                  bg: "bg-slate-800/50", 
                  border: "border-slate-700/50"
                },
                {
                  title: "Projetos Ativos",
                  value: dashboardData.projetosAtivos,
                  change: "+4",
                  icon: Building2,
                  color: "text-slate-300",
                  bg: "bg-slate-800/50",
                  border: "border-slate-700/50"
                }
              ].map((metric, index) => {
                const IconComponent = metric.icon;
                return (
                  <Card key={index} className={`bg-slate-800/30 backdrop-blur-sm border ${metric.border} hover:bg-slate-800/50 transition-all duration-300`}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 ${metric.bg} rounded-xl flex items-center justify-center`}>
                          <IconComponent className={`h-6 w-6 ${metric.color}`} />
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-pulse"></div>
                          <span className="text-xs text-slate-400">Ao vivo</span>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm text-slate-400">{metric.title}</p>
                        <p className="text-3xl font-bold text-white">{metric.value}</p>
                        <p className="text-xs text-slate-400">{metric.change} nas últimas 24h</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center">
              <p className="text-slate-400 mb-8">Tecnologias integradas no Pipeline Moveleiro™:</p>
              <div className="flex justify-center items-center space-x-8">
                {['Meta Ads', 'WhatsApp', 'Notion', 'Make', 'Google Analytics'].map((tech, index) => (
                  <Badge key={index} variant="outline" className="border-slate-600 text-slate-400 px-4 py-2">
                    {tech}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section - Simplified */}
      <section 
        id="resultados"
        ref={setSectionRef('resultados')}
        className="py-24 bg-gradient-to-b from-slate-900 to-white"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-6xl mx-auto ${visibleSections.has('resultados') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                📈 Resultados Comprovados
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Nossos resultados falam por si
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Cada operação da TORQUE é acompanhada com dados reais, relatórios e transparência total.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  metric: "R$ 17-24",
                  label: "Custo por Lead",
                  description: "Média no setor moveleiro premium",
                  icon: Target
                },
                {
                  metric: "38%",
                  label: "Conversão Lead → Projeto", 
                  description: "4x acima da média do setor",
                  icon: TrendingUp
                },
                {
                  metric: "41%",
                  label: "Conversão Projeto → Venda",
                  description: "Taxa de fechamento Italínea",
                  icon: CheckCircle
                },
                {
                  metric: "+4.2x",
                  label: "ROI médio",
                  description: "Retorno comprovado sobre investimento",
                  icon: Zap
                }
              ].map((result, index) => {
                const IconComponent = result.icon;
                return (
                  <Card key={index} className="bg-white border hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                    <CardContent className="p-6 sm:p-8 text-center">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 border rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-slate-200 transition-colors duration-300">
                        <IconComponent className="h-6 w-6 sm:h-8 sm:w-8 text-slate-700" />
                      </div>
                      <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2 sm:mb-3">
                        {result.metric}
                      </div>
                      <p className="text-sm sm:text-base text-slate-800 font-semibold mb-1 sm:mb-2">{result.label}</p>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{result.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Remaining sections would continue with similar gradient transitions and refined styling... */}

      {/* About Section */}
      <section 
        id="sobre"
        ref={setSectionRef('sobre')}
        className="py-24 bg-white"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-6xl mx-auto ${visibleSections.has('sobre') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                  🏪 Nossa História
                </Badge>
                <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8">
                  Somos lojistas antes
                  <span className="block text-slate-700">de sermos consultores</span>
                </h2>
                
                <div className="space-y-6">
                  <div className="bg-slate-50 p-6 rounded-xl">
                    <p className="text-lg text-slate-700 leading-relaxed">
                      A TORQUE Marketing nasceu dentro da <strong className="text-slate-900">Italínea Umarizal</strong>, nossa operação própria de móveis planejados em <strong className="text-slate-900">Belém–PA</strong>.
                    </p>
                  </div>
                  
                  <div className="bg-slate-50 p-6 rounded-xl">
                    <p className="text-lg text-slate-700 leading-relaxed">
                      <strong className="text-slate-900">Aplicamos nos clientes o mesmo método que usamos para vender todos os dias.</strong> Falamos a linguagem do setor: medição, briefing, projeto e fechamento.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm border">
                      <div className="text-2xl font-bold text-slate-700">2+</div>
                      <div className="text-sm text-slate-600">Lojas Próprias</div>
                    </div>
                    <div className="text-center p-4 bg-white rounded-lg shadow-sm border">
                      <div className="text-2xl font-bold text-slate-700">R$ 3.2M+</div>
                      <div className="text-sm text-slate-600">Em Vendas Geradas</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_torque-marketing/artifacts/gipykimu_image.png"
                  alt="Showroom Italínea - Suíte planejada"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 backdrop-blur-sm p-4 rounded-lg">
                    <p className="text-sm font-medium text-slate-800">
                      "Gerimos operações reais de móveis planejados — aplicamos no marketing dos clientes o mesmo método que usamos para vender"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contato"
        ref={setSectionRef('contato')}
        className="py-24 bg-gradient-to-b from-white to-gray-50"
      >
        <div className="container mx-auto px-6">
          <div className={`max-w-6xl mx-auto ${visibleSections.has('contato') ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="text-center mb-16">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-6">
                🚀 Comece Agora
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
                Pronto para aplicar o que funciona
                <span className="block text-slate-700">na sua loja de planejados?</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Solicite um diagnóstico gratuito e veja como o Pipeline Moveleiro™ pode funcionar para o seu negócio.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-16">
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-8">Entre em contato</h3>
                  
                  <div className="space-y-6">
                    <a 
                      href="https://wa.me/559184070470" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-6 p-6 bg-white rounded-2xl hover:shadow-lg transition-all duration-300 group border"
                    >
                      <div className="w-16 h-16 bg-green-600 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <MessageCircle className="h-8 w-8" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-lg">WhatsApp Direto</p>
                        <p className="text-green-600 font-medium">(91) 8407-0470</p>
                        <p className="text-sm text-slate-600">Resposta em até 2 horas úteis</p>
                      </div>
                    </a>

                    <a 
                      href="mailto:macleyton@torquedigital.com.br"
                      className="flex items-center space-x-6 p-6 bg-white rounded-2xl hover:shadow-lg transition-all duration-300 group border"
                    >
                      <div className="w-16 h-16 bg-slate-600 text-white rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Mail className="h-8 w-8" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-lg">E-mail Comercial</p>
                        <p className="text-slate-600 font-medium">macleyton@torquedigital.com.br</p>
                        <p className="text-sm text-slate-600">Para propostas e parcerias</p>
                      </div>
                    </a>

                    <div className="flex items-center space-x-6 p-6 bg-white rounded-2xl border">
                      <div className="w-16 h-16 bg-slate-600 text-white rounded-2xl flex items-center justify-center">
                        <MapPin className="h-8 w-8" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-lg">Showroom Italínea</p>
                        <p className="text-slate-600">Rua Boaventura da Silva, 771, Sala A<br />Nazaré - Belém/PA</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Card className="bg-white border shadow-xl">
                <CardContent className="p-10">
                  <div className="mb-8">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">Diagnóstico Gratuito</h3>
                    <p className="text-slate-600">Preencha o formulário e nossa equipe fará uma análise personalizada do seu negócio</p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="nome" className="text-slate-700 font-medium">Nome Completo *</Label>
                      <Input
                        id="nome"
                        value={formData.nome}
                        onChange={(e) => handleInputChange('nome', e.target.value)}
                        required
                        className="bg-white border h-12 text-lg focus:border-slate-400"
                        placeholder="Seu nome completo"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="empresa" className="text-slate-700 font-medium">Empresa / Loja *</Label>
                      <Input
                        id="empresa"
                        value={formData.empresa}
                        onChange={(e) => handleInputChange('empresa', e.target.value)}
                        required
                        className="bg-white border h-12 text-lg focus:border-slate-400"
                        placeholder="Nome da sua empresa"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone" className="text-slate-700 font-medium">WhatsApp *</Label>
                      <Input
                        id="telefone"
                        type="tel"
                        value={formData.telefone}
                        onChange={(e) => handleInputChange('telefone', e.target.value)}
                        required
                        className="bg-white border h-12 text-lg focus:border-slate-400"
                        placeholder="(00) 00000-0000"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="cidade" className="text-slate-700 font-medium">Cidade *</Label>
                      <Input
                        id="cidade"
                        value={formData.cidade}
                        onChange={(e) => handleInputChange('cidade', e.target.value)}
                        required
                        className="bg-white border h-12 text-lg focus:border-slate-400"
                        placeholder="Sua cidade"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="interesse" className="text-slate-700 font-medium">Principal Interesse</Label>
                      <Select onValueChange={(value) => handleInputChange('interesse', value)}>
                        <SelectTrigger className="bg-white border h-12 text-lg focus:border-slate-400">
                          <SelectValue placeholder="Selecione seu maior interesse" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pipeline">🚀 Implementar Pipeline Moveleiro™ completo</SelectItem>
                          <SelectItem value="anuncios">📱 Otimizar campanhas Meta Ads</SelectItem>
                          <SelectItem value="automacao">⚡ Automação WhatsApp + CRM</SelectItem>
                          <SelectItem value="consultoria">🎯 Consultoria em Marketing Digital</SelectItem>
                          <SelectItem value="mentoria">👥 Mentoria TORQUE (Sócio Estratégico)</SelectItem>
                          <SelectItem value="outro">💬 Outro (descrever no contato)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white py-4 text-lg font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                    >
                      <MousePointer className="mr-2 h-5 w-5" />
                      Solicitar Diagnóstico Gratuito
                    </Button>
                  </form>

                  <div className="mt-6 p-4 bg-slate-50 border rounded-lg">
                    <p className="text-sm text-slate-700">
                      ✅ Análise gratuita do seu funil atual<br />
                      ✅ Identificação de oportunidades<br />
                      ✅ Proposta personalizada sem compromisso
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-4 gap-12">
            <div className="lg:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_4c7aa62d-bdd7-432c-87e3-51085f7d8bab/artifacts/2urfcdrd_logo-torque-digital-branco.png" 
                alt="TORQUE Digital" 
                className="h-12 mb-8"
              />
              <blockquote className="text-xl italic text-slate-300 mb-8 font-light leading-relaxed">
                "Se não vende, não é criativo.<br />
                <span className="text-white font-medium">Você precisa de um funil que funciona."</span>
              </blockquote>
              
              <div className="space-y-4">
                <p className="text-slate-400">
                  <strong className="text-white">TORQUE Marketing e Gestão LTDA</strong><br />
                  Transformando marketing em vendas previsíveis para o setor moveleiro
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold text-xl mb-6 text-white">Contato Direto</h4>
              <div className="space-y-4 text-slate-300">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-slate-400" />
                  <span>macleyton@torquedigital.com.br</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-slate-400" />
                  <span>(91) 8407-0470</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold text-xl mb-6 text-white">Localização</h4>
              <div className="text-slate-300 space-y-2">
                <p><strong>Italínea Umarizal</strong></p>
                <p>Rua Boaventura da Silva, 771</p>
                <p>Sala A, Nazaré</p>
                <p>Belém/PA - Brasil</p>
                <p className="text-sm text-slate-400 mt-4">CEP: 66035-000</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-700 mt-12 pt-8">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
              <p className="text-slate-400 text-sm">
                © 2024 TORQUE Marketing e Gestão LTDA. Todos os direitos reservados.
              </p>
              <div className="flex space-x-6">
                <a 
                  href="https://www.instagram.com/torquemarketing.com.br/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center hover:bg-slate-700 transition-colors duration-300 transform hover:scale-110"
                >
                  <Instagram className="h-6 w-6" />
                </a>
                <a 
                  href="https://wa.me/559184070470" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center hover:bg-green-600 transition-colors duration-300 transform hover:scale-110"
                >
                  <MessageCircle className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EnhancedTorqueLandingPage;